/* ═══════════════════════════════════════════════════════════════
   Module Library — the firm's reusable block-groups
   (docs/MASTERS_UNIFIED_PLAN.md Phase 4).

   ⛔ A MODULE is a reusable block-group saved out of ONE spec. It is NOT a
   variant (that is a client version of a master). Never swap the words.

   Browse · preview · rename · archive/restore · delete. Placing a module
   into a spec is Phase 5 — the preview here is deliberately READ-ONLY.

   Every action names its permission: modules.view to be here at all,
   modules.edit to rename, modules.archive to archive or restore,
   modules.delete to destroy. The server checks the same keys through the
   generic table path (TABLE_PERMS.module_library → `modules`), so a hidden
   button is a courtesy, never the enforcement.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Column } from '../components/DataTable';
import { ModuleBrowser } from '../components/ModuleBrowser';
import { ModulePreview } from '../components/ModulePreview';
import { ModuleCategorySelect } from '../components/ModuleCategorySelect';
import { ModuleTagsInput } from '../components/ModuleTagsInput';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Switch } from '../components/ui/switch';
import { Icon } from '../components/ui/Icon';
import { ResizeHandle } from '../components/ui/resize-handle';
import { ChevronRight, PanelRight } from 'lucide-react';
import { Prefs } from '../services/prefsService';
import EmptyState from '../components/EmptyState';
import { StatusBadge } from '../components/StatusBadge';
import { rem } from '../lib/rem';
import { toAddress } from '../routing/address';
import { useAddressSync } from '../routing/useAddressSync';
import { isDraftPending, ModuleService } from '../services/moduleService';
import { useTableSync } from '../hooks/useTableSync';
import { hasPermission } from '../../shared/permissions';
import { nextSelectionByKey } from '../lib/listSelection';
import {
  moduleCountByCategory,
  normalizeTags,
  type ModuleCategory,
} from '../../shared/module-categories';
import {
  copyBlockForModule,
  moduleBlockCount,
  type ModuleTag,
  type SpecModule,
} from '../../shared/spec-modules';
import { tagSuggestionsFrom } from '../../shared/module-tags';
import { summarizeBulkResult, type BulkOutcome } from '../lib/moduleBrowserColumns';
import { ModuleBuilder, type ModuleBuilderDraft } from '../spec-studio/ModuleBuilder';

export interface ModuleLibraryProps {
  toast: (msg: string, isError?: boolean) => void;
  userName: string;
  userPermissions?: string[];
  userId?: string;
  isDark?: boolean;
}

// ─── Inline icons (no emoji as icons — DESIGN §5) ───
// Icons follow the text beside them (DESIGN.md §3.6): `em` sizes relative to
// the inherited font-size; `size` (px) stays as a fallback for any caller
// that hasn't migrated.
interface LocalIconProps {
  size?: number;
  em?: number;
}
function SvgIcon({ size = 14, em, children }: LocalIconProps & { children: React.ReactNode }) {
  const dim = em != null ? `${em}em` : size;
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}
const CopyIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </SvgIcon>
);
const ModuleIcon = ({ size = 28, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="5" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <rect x="13" y="10" width="8" height="11" rx="1" />
  </SvgIcon>
);

/** What went wrong, in the user's words — never a raw `any`. */
const reason = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

export function ModuleLibrary({
  toast,
  userName,
  userPermissions = [],
  userId,
  isDark = false,
}: ModuleLibraryProps) {
  const permCan = (key: string) => hasPermission(userPermissions, key);
  const canCreate = permCan('modules.create');
  const canArchive = permCan('modules.archive');
  const canEditModules = permCan('modules.edit');

  const [modules, setModules] = React.useState<SpecModule[]>([]);
  // Fed by ModuleBrowser's own fetch (`onCategoriesChange`) — the "Category
  // and tags…" dialogs below need the tree for their pickers, and this way
  // there is only ONE `module_categories` fetch on this page, not two.
  const [categories, setCategories] = React.useState<ModuleCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showArchived, setShowArchived] = React.useState(false);
  const [selection, setSelection] = React.useState<string[]>([]);
  const [anchor, setAnchor] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  // R2 Phase 4: the page is library AND builder. A row opens in the builder;
  // "New module" opens a blank one. The list below is untouched — browse,
  // rename, copy, archive and delete all still live there.
  const [builder, setBuilder] = React.useState<ModuleBuilderDraft | null>(null);

  // Explorer semantics need the modifier keys, and DataTable's row click hands
  // over the row only — so the last mousedown inside this page carries them.
  const modsRef = React.useRef({ ctrl: false, shift: false });

  // ─── Dialog focus return (modules-polish fix-up, item 7) ───
  // Radix restores focus to "whatever was focused when the dialog opened"
  // — but the "Category and tags…" cell button and the bulk "Set
  // category…" button both live inside rows/bars that can be gone by the
  // time the dialog closes (a table reload after Save; the bulk bar
  // itself unmounts once `clear()` empties the selection). Captured here,
  // at the exact moment each dialog is asked to open — `document.
  // activeElement` IS the trigger right then — and restored explicitly on
  // close; the page root (always present, `tabIndex={-1}`) is the fallback
  // once the trigger is confirmed gone.
  const pageRef = React.useRef<HTMLDivElement>(null);
  const editMetaTriggerRef = React.useRef<HTMLElement | null>(null);
  const bulkCategoryTriggerRef = React.useRef<HTMLElement | null>(null);
  function restoreFocusAfterDialog(triggerRef: { current: HTMLElement | null }) {
    const el = triggerRef.current;
    if (el && document.body.contains(el)) el.focus();
    else pageRef.current?.focus();
    triggerRef.current = null;
  }

  const load = React.useCallback(async () => {
    try {
      setError(null);
      const rows = await ModuleService.listAll();
      setModules(rows);
    } catch (err) {
      setError(reason(err, 'The module library could not be loaded'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);
  useTableSync(['module_library'], () => void load());

  // Migration 060 — the tag catalog feeds the tag pickers. A failed fetch
  // (older server) leaves the picker on the tags already in use, never blank.
  const [tagCatalog, setTagCatalog] = React.useState<ModuleTag[]>([]);
  const tagCatalogNames = React.useMemo(() => tagCatalog.map((t) => t.name), [tagCatalog]);
  const loadTagCatalog = React.useCallback(async () => {
    const [res] = await Promise.allSettled([ModuleService.listTags()]);
    if (res.status === 'fulfilled') setTagCatalog(res.value);
    else console.error('[ModuleLibrary] tag catalog failed to load', res.reason);
  }, []);
  React.useEffect(() => {
    void loadTagCatalog();
  }, [loadTagCatalog]);
  useTableSync(['module_tags'], () => void loadTagCatalog());
  const ensureTags = (tags: string[]) =>
    void ModuleService.ensureTagsInCatalog(tags, userName).catch((err) =>
      console.error('[ModuleLibrary] tags could not be added to the catalog', err)
    );

  const visible = React.useMemo(
    () => modules.filter((m) => (showArchived ? true : !m.archivedAt)),
    [modules, showArchived]
  );
  const visibleIds = React.useMemo(() => visible.map((m) => m.id), [visible]);

  // A row that scrolled out of the filter can never stay selected — that is
  // how a bulk action reaches something the user cannot see.
  React.useEffect(() => {
    setSelection((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds]);

  const selected = React.useMemo(
    () => visible.filter((m) => selection.includes(m.id)),
    [visible, selection]
  );
  const selectedIdSet = React.useMemo(() => new Set(selection), [selection]);
  const previewed = selected.length === 1 ? selected[0] : null;

  // Preview pane show/hide — remembered per user; default hidden.
  const [previewShown, setPreviewShownState] = React.useState<boolean>(() => {
    const saved = Prefs.get('module-library.previewShown');
    return typeof saved === 'boolean' ? saved : false;
  });
  const setPreviewShown = (v: boolean) => {
    setPreviewShownState(v);
    Prefs.set('module-library.previewShown', v);
  };

  // Pane width is a FRACTION of the workspace (panels are fractions, never
  // pixels): >= 16rem, <= 55%, and the table always keeps >= half.
  const workspaceRef = React.useRef<HTMLDivElement>(null);
  const [workspaceW, setWorkspaceW] = React.useState(0);
  React.useEffect(() => {
    const el = workspaceRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWorkspaceW(el.getBoundingClientRect().width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const remPx = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const maxFrac = Math.min(0.55, 0.5);
  const minFrac = workspaceW > 0 ? Math.min((16 * remPx()) / workspaceW, maxFrac) : 0.2;
  const clampFrac = (f: number) => Math.min(maxFrac, Math.max(minFrac, f));
  const [storedFrac, setStoredFrac] = React.useState<number>(() => {
    const saved = Prefs.get('module-library.previewWidth');
    return typeof saved === 'number' && saved > 0 && saved < 1 ? saved : 0.3;
  });
  const [dragFrac, setDragFrac] = React.useState<number | null>(null);
  const previewFrac = clampFrac(dragFrac ?? storedFrac);
  const dragStartFrac = React.useRef(0);
  const liveFrac = React.useRef(0.3);
  const commitFrac = (f: number) => {
    const c = clampFrac(f);
    setStoredFrac(c);
    setDragFrac(null);
    Prefs.set('module-library.previewWidth', c);
  };
  // The pane sits on the RIGHT, so dragging the handle left (dx < 0) widens it.
  const nudgeFrac = (dxPx: number) => {
    if (workspaceW > 0) commitFrac(previewFrac - dxPx / workspaceW);
  };

  // Address: the module open in the builder; Back from it closes the builder.
  // `/modules/<id>` has no deep-link consumer, so this page opens it itself.
  const [pendingModuleId, setPendingModuleId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!pendingModuleId || modules.length === 0) return;
    const target = modules.find((m) => m.id === pendingModuleId);
    if (target) setBuilder({ module: target });
    setPendingModuleId(null);
  }, [pendingModuleId, modules]);
  useAddressSync({
    address: builder?.module?.id
      ? toAddress({ kind: 'module', moduleId: builder.module.id })
      : null,
    listPath: '/modules',
    onLeave: () => setBuilder(null),
    onOpenId: setPendingModuleId,
    title: builder?.module?.id ? builder.module.name || null : null,
  });

  const onRowClick = (m: SpecModule) => {
    // A PLAIN click opens the module in the builder (Phase 4). Ctrl and Shift
    // keep their Explorer meaning — that is how the bulk archive and delete
    // above still get a selection to act on, and how the preview is reached.
    const mods = modsRef.current;
    if (!mods.ctrl && !mods.shift) {
      setBuilder({ module: m });
      return;
    }
    const next = nextSelectionByKey(selection, anchor, m.id, visibleIds, mods);
    setSelection(next.selection);
    setAnchor(next.anchor);
  };

  // Rename now lives inside ModuleBuilder (row click opens it) — same
  // ModuleService.rename call, no separate mini-dialog on this page.

  const doArchive = async (targets: SpecModule[], archived: boolean) => {
    if (!targets.length) return;
    setBusy(true);
    try {
      for (const m of targets) await ModuleService.setArchived(m.id, archived, userName);
      toast(
        `${archived ? 'Archived' : 'Restored'} ${targets.length} module${
          targets.length === 1 ? '' : 's'
        }`
      );
      await load();
    } catch (err) {
      toast(reason(err, `Those modules could not be ${archived ? 'archived' : 'restored'}`), true);
    } finally {
      setBusy(false);
    }
  };

  /** "Save a copy" — a whole new library row, owned by whoever clicked it.
   *  Forks ownership (Ruling 5): unlike Phase 3's per-paragraph update merge,
   *  this makes an entirely independent definition at v1, never linked back
   *  to the original. Every chunk block is re-minted with a fresh id — the
   *  same stamped-copy law `copyBlockForModule` already enforces for a spec
   *  capture applies here too: two library rows never share block identity. */
  const doDuplicate = async (m: SpecModule) => {
    setBusy(true);
    try {
      const nowIso = new Date().toISOString();
      const copy: SpecModule = {
        id: crypto.randomUUID(),
        name: `Copy of ${m.name}`,
        description: m.description,
        chunks: (m.chunks || []).map((chunk) => ({
          anchor: chunk.anchor,
          blocks: (chunk.blocks || []).map((b) => copyBlockForModule(b, crypto.randomUUID())),
        })),
        version: 1,
        sourceSpecId: m.sourceSpecId,
        sourceCsi: m.sourceCsi,
        archivedAt: null,
        createdBy: userName,
        createdAt: nowIso,
        modifiedBy: userName,
        lastModified: nowIso,
      };
      await ModuleService.create(copy);
      toast(`Saved a copy as “${copy.name}”`);
      await load();
    } catch (err) {
      toast(reason(err, 'That module could not be duplicated'), true);
    } finally {
      setBusy(false);
    }
  };

  // ─── "Category and tags…" — single row (Category/Tags cell click) ───
  const [editMetaTarget, setEditMetaTarget] = React.useState<SpecModule | null>(null);
  const [editMetaCategoryId, setEditMetaCategoryId] = React.useState<string | null>(null);
  const [editMetaTags, setEditMetaTags] = React.useState<string[]>([]);
  const [editMetaBusy, setEditMetaBusy] = React.useState(false);
  const [editMetaError, setEditMetaError] = React.useState<string | null>(null);
  const tagSuggestions = React.useMemo(
    () => tagSuggestionsFrom(tagCatalog, modules),
    [tagCatalog, modules]
  );

  const openEditMeta = (m: SpecModule) => {
    editMetaTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setEditMetaTarget(m);
    setEditMetaCategoryId(m.categoryId ?? null);
    setEditMetaTags(m.tags || []);
    setEditMetaError(null);
  };
  const doSaveEditMeta = async () => {
    if (!editMetaTarget) return;
    setEditMetaBusy(true);
    setEditMetaError(null);
    try {
      await ModuleService.updateMeta(
        editMetaTarget.id,
        { categoryId: editMetaCategoryId, tags: editMetaTags },
        userName
      );
      ensureTags(editMetaTags);
      toast(`Saved category and tags for "${editMetaTarget.name}"`);
      setEditMetaTarget(null);
      await load();
    } catch (err) {
      setEditMetaError(reason(err, 'That could not be saved'));
    } finally {
      setEditMetaBusy(false);
    }
  };

  // ─── Bulk: Set category… / Add tags… / Clear tags… (Explorer-style, the
  // same bulk bar the archive/delete actions already use) ───
  //
  // Each loop is sequential and keeps going past a failed item — a stale
  // row or a one-off save hiccup on module #3 never costs modules #4-#20
  // their update — UNLESS the failure is the user's own session going bad
  // (401/403), where continuing would just fail every remaining item the
  // same way for no reason. Either way the list reloads so the screen
  // shows what was really saved, and the toast says exactly that:
  // `summarizeBulkResult` (moduleBrowserColumns.ts) turns the per-item
  // outcomes into the one message, named failures and all.
  type BulkTarget = { items: SpecModule[]; clear: () => void };
  const isAuthError = (err: unknown): boolean => {
    const status = (err as { status?: number } | null | undefined)?.status;
    return status === 401 || status === 403;
  };

  const [bulkCategory, setBulkCategory] = React.useState<BulkTarget | null>(null);
  const [bulkCategoryValue, setBulkCategoryValue] = React.useState<string | null>(null);
  const [bulkCategoryBusy, setBulkCategoryBusy] = React.useState(false);
  const [bulkCategoryError, setBulkCategoryError] = React.useState<string | null>(null);
  // Module count per category — the bulk "move to" picker's right-aligned
  // count column (same helper `useModuleCategoryBrowser` uses for the
  // category cards).
  const bulkCategoryCounts = React.useMemo(
    () => moduleCountByCategory(visible, categories),
    [visible, categories]
  );
  const bulkCategoryTargetName =
    bulkCategoryValue == null
      ? null
      : categories.find((c) => c.id === bulkCategoryValue)?.name || null;
  const doBulkCategory = async () => {
    if (!bulkCategory) return;
    setBulkCategoryBusy(true);
    setBulkCategoryError(null);
    const outcomes: BulkOutcome[] = [];
    for (const m of bulkCategory.items) {
      try {
        await ModuleService.updateMeta(m.id, { categoryId: bulkCategoryValue }, userName);
        outcomes.push({ name: m.name });
      } catch (err) {
        outcomes.push({ name: m.name, error: reason(err, 'That could not be saved') });
        if (isAuthError(err)) break;
      }
    }
    await load();
    const { message, isError } = summarizeBulkResult(
      outcomes,
      (n) => `Set category for ${n} module${n === 1 ? '' : 's'}`
    );
    toast(message, isError);
    bulkCategory.clear();
    setBulkCategory(null);
    setBulkCategoryBusy(false);
  };

  const [bulkAddTags, setBulkAddTags] = React.useState<BulkTarget | null>(null);
  const [bulkAddTagsValue, setBulkAddTagsValue] = React.useState<string[]>([]);
  const [bulkAddTagsBusy, setBulkAddTagsBusy] = React.useState(false);
  const doBulkAddTags = async () => {
    if (!bulkAddTags || bulkAddTagsValue.length === 0) return;
    setBulkAddTagsBusy(true);
    const outcomes: BulkOutcome[] = [];
    for (const m of bulkAddTags.items) {
      try {
        await ModuleService.updateMeta(
          m.id,
          { tags: normalizeTags([...(m.tags || []), ...bulkAddTagsValue]) },
          userName
        );
        outcomes.push({ name: m.name });
      } catch (err) {
        outcomes.push({ name: m.name, error: reason(err, 'Those tags could not be added') });
        if (isAuthError(err)) break;
      }
    }
    if (outcomes.some((o) => !o.error)) ensureTags(bulkAddTagsValue);
    await load();
    const { message, isError } = summarizeBulkResult(
      outcomes,
      (n) => `Added tags to ${n} module${n === 1 ? '' : 's'}`
    );
    toast(message, isError);
    bulkAddTags.clear();
    setBulkAddTags(null);
    setBulkAddTagsValue([]);
    setBulkAddTagsBusy(false);
  };

  const [bulkClearTags, setBulkClearTags] = React.useState<BulkTarget | null>(null);
  const [bulkClearTagsBusy, setBulkClearTagsBusy] = React.useState(false);
  const doBulkClearTags = async () => {
    if (!bulkClearTags) return;
    setBulkClearTagsBusy(true);
    const outcomes: BulkOutcome[] = [];
    for (const m of bulkClearTags.items) {
      try {
        await ModuleService.updateMeta(m.id, { tags: [] }, userName);
        outcomes.push({ name: m.name });
      } catch (err) {
        outcomes.push({ name: m.name, error: reason(err, 'Those tags could not be cleared') });
        if (isAuthError(err)) break;
      }
    }
    await load();
    const { message, isError } = summarizeBulkResult(
      outcomes,
      (n) => `Cleared tags on ${n} module${n === 1 ? '' : 's'}`
    );
    toast(message, isError);
    bulkClearTags.clear();
    setBulkClearTags(null);
    setBulkClearTagsBusy(false);
  };

  const columns: Column<SpecModule>[] = [
    {
      key: 'name',
      label: 'Name',
      width: 200,
      render: (m) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span
            aria-hidden="true"
            style={{
              width: rem(6),
              height: rem(6),
              borderRadius: '50%',
              background: selection.includes(m.id) ? 'var(--ac)' : 'transparent',
            }}
          />
          <span style={{ fontWeight: 600, color: 'var(--tx)' }}>{m.name}</span>
        </span>
      ),
      getValue: (m) => m.name || '',
    },
    {
      key: 'description',
      label: 'Description',
      width: 200,
      getValue: (m) => m.description || '',
      defaultHidden: true,
    },
    {
      key: 'chunks',
      label: 'Chunks',
      width: 70,
      getValue: (m) => (m.chunks || []).length,
      render: (m) => <>{(m.chunks || []).length}</>,
      defaultHidden: true,
    },
    {
      key: 'blocks',
      label: 'Blocks',
      width: 70,
      getValue: (m) => moduleBlockCount(m),
      render: (m) => <>{moduleBlockCount(m)}</>,
      defaultHidden: true,
    },
    {
      key: 'version',
      label: 'Version',
      width: 70,
      getValue: (m) => m.version || 1,
      render: (m) => <>v{m.version || 1}</>,
      defaultHidden: true,
    },
    {
      key: 'lastModified',
      label: 'Modified',
      width: 140,
      getValue: (m) => m.lastModified || '',
      render: (m) => (
        <span style={{ color: 'var(--tm)' }}>
          {fmtDate(m.lastModified)}
          {m.modifiedBy ? ` · ${m.modifiedBy}` : ''}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      getValue: (m) =>
        m.archivedAt ? 'archived' : isDraftPending(m) ? 'pending_publish' : 'published',
      render: (m) =>
        m.archivedAt ? (
          <StatusBadge status="archived" />
        ) : isDraftPending(m) ? (
          <span title="A draft is waiting to be published" style={{ display: 'inline-flex' }}>
            <StatusBadge status="pending_publish" label="Pending publish" variant="info" />
          </span>
        ) : (
          <StatusBadge status="published" />
        ),
    },
  ];

  // The builder takes the whole page while it is open — it IS the module, and
  // the sheet is the hero (DESIGN §3.4). Nothing about the list is unmounted
  // for good: closing returns to it, refreshed if anything was saved.
  if (builder) {
    return (
      <ModuleBuilder
        draft={builder}
        userName={userName}
        userId={userId}
        isDark={isDark}
        userPermissions={userPermissions}
        toast={toast}
        onClose={() => setBuilder(null)}
        onSaved={() => void load()}
      />
    );
  }

  return (
    <div
      ref={pageRef}
      tabIndex={-1}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      onMouseDownCapture={(e) => {
        modsRef.current = { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey };
      }}
    >
      {/* ── Toolbar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-5)',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sf)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontWeight: 700, color: 'var(--tx)' }}>Modules</div>
        <div style={{ fontSize: 'calc(var(--font-size-base) * 12 / 13)', color: 'var(--tm)' }}>
          Reusable block-groups saved out of specs
        </div>
        <div style={{ flex: 1 }} />

        {canCreate && (
          <Button size="sm" onClick={() => setBuilder({ module: null })}>
            New module
          </Button>
        )}

        <Button
          variant={showArchived ? 'secondary' : 'outline'}
          size="sm"
          aria-pressed={showArchived}
          onClick={() => setShowArchived((v) => !v)}
        >
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      </div>

      {/* ── Body: list + read-only preview ── */}
      <div ref={workspaceRef} style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 'var(--space-4)' }}>
          {loading ? (
            <div role="status" aria-busy="true" style={{ padding: 'var(--space-5)' }}>
              <span className="sr-only">Loading the module library…</span>
              <div
                aria-hidden="true"
                style={{
                  height: rem(220),
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                }}
              />
            </div>
          ) : error ? (
            <EmptyState
              icon={<ModuleIcon em={2.1538} />}
              title="The module library could not be loaded"
              subtitle={error}
              action={{ label: 'Try again', onClick: () => void load() }}
            />
          ) : modules.length === 0 ? (
            <EmptyState
              icon={<ModuleIcon em={2.1538} />}
              title="No modules yet"
              subtitle="Start one with “New module”, or open a spec in Spec Studio, select blocks with the gutter grips and choose “Save as module…” — either way it lands here for the whole firm to reuse."
            />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<ModuleIcon em={2.1538} />}
              title="Every module is archived"
              subtitle="Turn on “Show archived” to see them."
              action={{ label: 'Show archived', onClick: () => setShowArchived(true) }}
            />
          ) : (
            <ModuleBrowser
              hostId="library"
              layout="rail"
              modules={visible}
              catalogTags={tagCatalogNames}
              userName={userName}
              userPermissions={userPermissions}
              toast={toast}
              storageKeyBase="module-library-table.v2"
              columns={columns}
              onRowClick={onRowClick}
              rowHref={(m) => toAddress({ kind: 'module', moduleId: m.id })}
              searchPlaceholder="Search modules, categories, tags…"
              initialSort={{ key: 'name', direction: 'asc' }}
              selectedIds={selectedIdSet}
              onSelectionChange={(ids) => setSelection([...ids])}
              onEditModuleMeta={canEditModules ? openEditMeta : undefined}
              onCategoriesChange={setCategories}
              toolbarExtra={
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    minHeight: rem(32),
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--tx)',
                  }}
                >
                  Preview
                  <Switch
                    checked={previewShown}
                    onCheckedChange={setPreviewShown}
                    aria-label="Show module preview"
                  />
                </label>
              }
              emptyAction={
                canCreate
                  ? { label: 'New module', onClick: () => setBuilder({ module: null }) }
                  : undefined
              }
              bulkActions={{
                archive: {
                  permission: 'modules.archive',
                  onArchive: (items) => doArchive(items, true),
                  onUndo: (items) => doArchive(items, false),
                  label: 'Archive',
                },
                delete: {
                  permission: 'modules.delete',
                  onDelete: async (items) => {
                    for (const m of items) await ModuleService.remove(m.id);
                    toast(`Deleted ${items.length} module${items.length === 1 ? '' : 's'}`);
                    setSelection([]);
                    await load();
                  },
                  confirmTitle: 'Delete these modules permanently?',
                  confirmDescription: (n) => (
                    <>
                      {n === 1 ? 'This module is' : `These ${n} modules are`} deleted permanently,
                      for the whole firm. Specs that already use them are unaffected — every
                      insertion is its own copy. Archive instead if you only want them out of the
                      way.
                    </>
                  ),
                },
                exportCsv: { permission: 'modules.export', filename: 'modules' },
                extra: (items, meta) => {
                  const archivedOnes = items.filter((m) => m.archivedAt);
                  return (
                    <>
                      {canCreate && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={async () => {
                            for (const m of items) await doDuplicate(m);
                            meta?.clear();
                          }}
                        >
                          <CopyIcon em={1} /> Save a copy
                        </Button>
                      )}
                      {archivedOnes.length > 0 && canArchive && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => void doArchive(archivedOnes, false)}
                        >
                          Restore {archivedOnes.length}
                        </Button>
                      )}
                      {canEditModules && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              bulkCategoryTriggerRef.current =
                                document.activeElement instanceof HTMLElement
                                  ? document.activeElement
                                  : null;
                              setBulkCategoryValue(null);
                              setBulkCategoryError(null);
                              setBulkCategory({ items, clear: () => meta?.clear() });
                            }}
                          >
                            Set category…
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setBulkAddTagsValue([]);
                              setBulkAddTags({ items, clear: () => meta?.clear() });
                            }}
                          >
                            Add tags…
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBulkClearTags({ items, clear: () => meta?.clear() })}
                          >
                            Clear tags
                          </Button>
                        </>
                      )}
                    </>
                  );
                },
              }}
            />
          )}
        </div>

        {previewShown ? (
          <div
            style={{
              position: 'relative',
              width: `${previewFrac * 100}%`,
              flexShrink: 0,
              display: 'flex',
              minHeight: 0,
            }}
          >
            <ResizeHandle
              aria-label="Resize module preview"
              valueNow={Math.round(previewFrac * 100)}
              valueMin={Math.round(minFrac * 100)}
              valueMax={Math.round(maxFrac * 100)}
              onResizeStart={() => {
                dragStartFrac.current = previewFrac;
                liveFrac.current = previewFrac;
              }}
              onResize={(dx) => {
                if (workspaceW <= 0) return;
                liveFrac.current = clampFrac(dragStartFrac.current - dx / workspaceW);
                setDragFrac(liveFrac.current);
              }}
              onResizeEnd={() => commitFrac(liveFrac.current)}
              onNudge={nudgeFrac}
              onJump={(to) => commitFrac(to === 'min' ? minFrac : maxFrac)}
              style={{
                position: 'absolute',
                left: 'calc(var(--space-2) / -2)',
                top: 0,
                bottom: 0,
                zIndex: 1,
              }}
            />
            <aside
              aria-label="Module preview"
              style={{
                flex: 1,
                minWidth: 0,
                borderLeft: '1px solid var(--bd)',
                background: 'var(--sf)',
                overflow: 'auto',
                padding: 'var(--space-4)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--tx)',
                  }}
                >
                  Preview
                </span>
                {previewed && canEditModules && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setBuilder({ module: previewed })}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Collapse preview"
                  title="Collapse preview"
                  onClick={() => setPreviewShown(false)}
                >
                  <Icon icon={ChevronRight} />
                </Button>
              </div>
              {previewed ? (
                <ModulePreview module={previewed} />
              ) : (
                <div
                  style={{
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tm)',
                    lineHeight: 1.6,
                  }}
                >
                  {selected.length > 1
                    ? `${selected.length} modules selected. Ctrl-click just one to preview it.`
                    : 'Click a module to open it in the builder. Ctrl-click one instead to preview the blocks it carries here, or Ctrl and Shift-click several to archive or delete them together.'}
                </div>
              )}
            </aside>
          </div>
        ) : (
          <div
            style={{
              width: 'var(--space-7)',
              flexShrink: 0,
              borderLeft: '1px solid var(--bd)',
              background: 'var(--sa)',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: 'var(--space-2)',
            }}
          >
            <Button
              variant="ghost"
              size="icon"
              aria-label="Show module preview"
              title="Preview"
              onClick={() => setPreviewShown(true)}
            >
              <Icon icon={PanelRight} />
            </Button>
          </div>
        )}
      </div>

      {/* ── Category and tags… (single row) ── */}
      <Dialog
        open={!!editMetaTarget}
        onOpenChange={(o) => !editMetaBusy && !o && setEditMetaTarget(null)}
      >
        <DialogContent
          className="max-w-sm"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            restoreFocusAfterDialog(editMetaTriggerRef);
          }}
        >
          <DialogHeader>
            <DialogTitle>Category and tags</DialogTitle>
            <DialogDescription>“{editMetaTarget?.name}”</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <label
                htmlFor="sf-modmeta-cat"
                style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tx)' }}
              >
                Category
              </label>
              <ModuleCategorySelect
                value={editMetaCategoryId}
                onChange={setEditMetaCategoryId}
                categories={categories}
                canCreate={canCreate}
                userName={userName}
                ariaLabel="Category"
                describedBy={editMetaError ? 'sf-modmeta-error' : undefined}
                onError={setEditMetaError}
              />
            </div>
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <label
                htmlFor="sf-modmeta-tags"
                style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tx)' }}
              >
                Tags
              </label>
              <ModuleTagsInput
                id="sf-modmeta-tags"
                value={editMetaTags}
                onChange={setEditMetaTags}
                suggestions={tagSuggestions}
                ariaLabel="Tags"
              />
            </div>
            {editMetaError && (
              <p
                id="sf-modmeta-error"
                role="alert"
                aria-live="assertive"
                style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
              >
                {editMetaError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditMetaTarget(null)}
              disabled={editMetaBusy}
            >
              Cancel
            </Button>
            <Button onClick={doSaveEditMeta} disabled={editMetaBusy}>
              {editMetaBusy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk: Set category… — a real move-to picker, not a plain
          dropdown: the category list is IN the dialog, always open. ── */}
      <Dialog
        open={!!bulkCategory}
        onOpenChange={(o) => !bulkCategoryBusy && !o && setBulkCategory(null)}
      >
        <DialogContent
          className="max-w-sm"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            restoreFocusAfterDialog(bulkCategoryTriggerRef);
          }}
        >
          <DialogHeader>
            <DialogTitle>
              Set category for {bulkCategory?.items.length} module
              {bulkCategory?.items.length === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const names = bulkCategory?.items.map((m) => m.name) || [];
                if (names.length <= 2) return names.join(', ');
                return `${names.slice(0, 2).join(', ')}, +${names.length - 2} more`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: rem(280),
              overflow: 'hidden',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <ModuleCategorySelect
              inline
              value={bulkCategoryValue}
              onChange={setBulkCategoryValue}
              categories={categories}
              countByCategory={bulkCategoryCounts}
              canCreate={canCreate}
              userName={userName}
              ariaLabel="Search categories"
              createHint={
                canCreate
                  ? categories.length === 0
                    ? 'No categories yet — type a name above to create one.'
                    : 'Type a path to create a new category.'
                  : undefined
              }
              describedBy={bulkCategoryError ? 'sf-modbulk-cat-error' : undefined}
              onError={setBulkCategoryError}
            />
          </div>
          {bulkCategoryError && (
            <p
              id="sf-modbulk-cat-error"
              role="alert"
              aria-live="assertive"
              style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
            >
              {bulkCategoryError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkCategory(null)}
              disabled={bulkCategoryBusy}
            >
              Cancel
            </Button>
            <Button onClick={doBulkCategory} disabled={bulkCategoryBusy}>
              {bulkCategoryBusy
                ? 'Saving…'
                : bulkCategoryTargetName
                  ? `Move to ${bulkCategoryTargetName}`
                  : 'Remove category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk: Add tags… ── */}
      <Dialog
        open={!!bulkAddTags}
        onOpenChange={(o) => !bulkAddTagsBusy && !o && setBulkAddTags(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add tags</DialogTitle>
            <DialogDescription>
              Added to {bulkAddTags?.items.length} module
              {bulkAddTags?.items.length === 1 ? '' : 's'}, alongside whatever tags each already
              has.
            </DialogDescription>
          </DialogHeader>
          <ModuleTagsInput
            value={bulkAddTagsValue}
            onChange={setBulkAddTagsValue}
            suggestions={tagSuggestions}
            ariaLabel="Tags to add"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkAddTags(null)}
              disabled={bulkAddTagsBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={doBulkAddTags}
              disabled={bulkAddTagsBusy || bulkAddTagsValue.length === 0}
            >
              {bulkAddTagsBusy ? 'Saving…' : 'Add tags'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk: Clear tags ── */}
      <ConfirmDialog
        open={!!bulkClearTags}
        onOpenChange={(o) => !bulkClearTagsBusy && !o && setBulkClearTags(null)}
        title="Clear tags?"
        description={
          <>
            Removes every tag from {bulkClearTags?.items.length} module
            {bulkClearTags?.items.length === 1 ? '' : 's'}. Categories are unaffected.
          </>
        }
        confirmLabel="Clear tags"
        busy={bulkClearTagsBusy}
        onConfirm={doBulkClearTags}
      />
    </div>
  );
}

export default ModuleLibrary;
